import { getControllerApiVersion } from './utils';

// This reconciles derived monitoring validity, not the owned settings fields in
// reconcile_state. The message handler calls it after slave reconciliation and
// API-version updates; metadata refresh also calls it without a telemetry packet.
// Keeping this boundary separate lets both paths invalidate stale layout/access
// state without coupling generic object reconciliation to controller capabilities.
// The monitoring array shares the controller's global Multi IO index map.
// Only matching state/settings/monitor layouts may be joined; replay is not fresh data.
export function reconcileOutputMonitor(controller, data, now = Date.now()) {
  // Industrial Pro uses its native API sequence, independently of ESP BAIC.
  if (
    (controller.type !== 6000 && getControllerApiVersion(controller) < 'BAIC') ||
    controller.access_rights?.multi_io?.read !== true
  ) {
    delete controller.output_monitor;
    delete controller.multi_io_state_layout;
    delete controller.multi_io_settings_layout;
    for (const slave of controller.slaves ?? []) {
      for (const port of slave.multi_outs ?? []) {
        delete port.monitor;
        delete port._monitor_layout;
      }
    }
    return;
  }
  for (const key of ['multi_io_state_layout', 'multi_io_settings_layout']) {
    if (Object.prototype.hasOwnProperty.call(data ?? {}, key))
      controller[key] =
        Number.isInteger(data[key]) && data[key] > 0 && data[key] <= 0xffffffff ? data[key] : 0;
  }
  // Record only mappings actually present in this settings message. An empty
  // or filtered settings body must not approve cached rows with old indexes.
  const settingsRows = new Map();
  if (
    data?.multi_io_counters &&
    Array.isArray(data.slaves) &&
    controller.multi_io_state_layout > 0 &&
    controller.multi_io_state_layout === controller.multi_io_settings_layout
  ) {
    for (const slave of data.slaves)
      for (const port of slave.multi_outs ?? []) {
        if (Number.isInteger(port.id) && Number.isInteger(port._index) && port.func !== undefined)
          settingsRows.set(`${slave.address}:${port.id}`, port._index);
      }
  }
  const incoming = data?.output_monitor;
  if (
    incoming?.schema_version === 4 &&
    Number.isInteger(incoming.sequence) &&
    incoming.sequence >= 0 &&
    incoming.sequence <= 0xffffffff &&
    Number.isInteger(incoming.layout_id) &&
    incoming.layout_id >= 0 &&
    incoming.layout_id <= 0xffffffff &&
    Array.isArray(incoming.outputs) &&
    incoming.outputs.length <= 256
  ) {
    const previous = controller.output_monitor;
    controller.output_monitor = {
      sequence: incoming.sequence,
      layout_id: incoming.layout_id,
      outputs: incoming.outputs,
      receivedAt:
        previous?.sequence === incoming.sequence && previous?.layout_id === incoming.layout_id
          ? previous.receivedAt
          : now
    };
  }
  const monitor = controller.output_monitor;
  const matching =
    monitor?.layout_id > 0 &&
    monitor.layout_id === controller.multi_io_state_layout &&
    monitor.layout_id === controller.multi_io_settings_layout;
  for (const slave of controller.slaves ?? []) {
    for (const port of slave.multi_outs ?? []) {
      const index = port._index;
      if (settingsRows.get(`${slave.address}:${port.id}`) === index && Number.isInteger(index))
        port._monitor_layout = controller.multi_io_settings_layout;
      const reading =
        matching &&
        port._monitor_layout === monitor.layout_id &&
        Number.isInteger(index) &&
        index >= 0
          ? monitor.outputs[index]
          : undefined;
      // The controller decides which readings apply; hardware metadata must not
      // override its statuses. Zero means no reading provided: hide that slot.
      if (monitor || port.monitor)
        port.monitor = reading
          ? { ...reading, receivedAt: monitor.receivedAt }
          : { reading_status: 0 };
    }
  }
}

export function hasOutputMonitorDisplay(monitor) {
  return Boolean(monitor && (monitor.reading_status !== 0 || monitor.condition_flags));
}

// Base units come from the shared protocol, never from a guessed output variant.
const extraUnits = {
  1: ['I', 'A'],
  2: ['U', 'V'],
  3: ['P', 'W'],
  4: ['PF', ''],
  5: ['φ', '°'],
  6: ['f', 'Hz'],
  7: ['S', 'VA'],
  8: ['Q', 'var']
};
function scaledValue(raw, scale, unit, signed) {
  if (
    !Number.isInteger(raw) ||
    raw < (signed ? -32768 : 0) ||
    raw > (signed ? 32767 : 65535) ||
    !Number.isInteger(scale) ||
    scale < -128 ||
    scale > 127
  )
    return null;
  if (unit === 'A' && scale <= -4) {
    unit = 'mA';
    scale += 3;
  }
  const value = raw * 10 ** scale;
  const text =
    scale < -6 || scale > 6 ? value.toExponential(2) : value.toFixed(Math.max(0, -scale));
  return unit ? `${text} ${unit}` : text;
}
export function formatOutputMonitor(monitor, t, now = Date.now()) {
  if (!hasOutputMonitorDisplay(monitor)) return '';
  const packed = monitor?.reading_status;
  if (!Number.isInteger(packed) || packed < 0 || packed > 255)
    return t('output_monitor_unavailable');
  const condition = monitor.condition_flags & 1 ? t('output_monitor_overcurrent_off') : '';
  const unknown = [packed & 0xc0, monitor.condition_flags & 0xfe]
    .filter(Boolean)
    .map((value) => `${t('unknown')} (${value})`)
    .join(' · ');
  // Publication is every five seconds; allow missed/delayed deliveries.
  if (!Number.isFinite(monitor.receivedAt) || now - monitor.receivedAt >= 15000)
    return t('output_monitor_stale');
  const slots = [
    { key: 'current', shift: 2, label: 'I', unit: 'A', signed: true },
    { key: 'voltage', shift: 0, label: 'U', unit: 'V', signed: false }
  ];
  if (Number.isInteger(monitor.info_type) && monitor.info_type > 0 && monitor.info_type <= 255) {
    const [label, unit] = extraUnits[monitor.info_type] ?? [
      `${t('output_monitor_reading')} ${t('unknown')} (${monitor.info_type})`,
      ''
    ];
    slots.push({ key: 'info', shift: 4, label, unit, signed: true });
  }
  const readings = slots
    .map(({ key, shift, label, unit, signed }) => {
      const status = (packed >> shift) & 3;
      // Zero means no reading is available for this slot, including unsupported quantities.
      if (status === 0) return null;
      const number =
        status === 1 ? scaledValue(monitor[key], monitor[`${key}_scale`], unit, signed) : null;
      if (number !== null) return { text: key === 'info' ? `${label}: ${number}` : number };
      const failure =
        status === 2
          ? 'output_monitor_stale'
          : status === 3
            ? 'output_monitor_error'
            : 'output_monitor_unavailable';
      return { text: `${label}: ${t(failure)}`, failure };
    })
    .filter(Boolean);
  // Collapse a common failure, but keep independent slot failures next to valid readings.
  const common = readings[0]?.failure;
  const text =
    common && readings.every((r) => r.failure === common)
      ? t(common)
      : readings.map((r) => r.text).join(' · ');
  return [text, condition, unknown].filter(Boolean).join(' · ');
}
